'use client';

import {
  Fragment,
  forwardRef,
  useState,
  type CSSProperties,
  type VideoHTMLAttributes,
} from 'react';
import { sanitizeMediaUrl } from '../url-safety';
import { useEditMode, type VideoValue } from './context';
import { getByPath } from './get-by-path';

type Props = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> & {
  path: string;
  value: VideoValue | undefined;
  className?: string;
  style?: CSSProperties;
  emptyLabel?: string;
};

export const EditableVideo = forwardRef<HTMLVideoElement, Props>(function EditableVideo(
  { path, value, className, style, emptyLabel = 'Adicionar vídeo', ...videoProps },
  ref,
) {
  const { editing, values, selectedPath, setSelected, renderVideoPicker } =
    useEditMode();
  const [open, setOpen] = useState(false);

  // Live content (Convex draft/published values) wins; the `value` prop is the
  // client's static default/fallback.
  const resolved = getByPath(values, path) as VideoValue | undefined;
  const effective = resolved ?? value;

  const src = sanitizeMediaUrl(effective?.url) ?? null;

  if (!editing) {
    if (!src) return null;
    return (
      <video
        ref={ref}
        src={src}
        className={className}
        style={style}
        {...videoProps}
      />
    );
  }

  const isSelected = selectedPath === path;
  const ringClass = isSelected
    ? 'ring-2 ring-blue-500 ring-offset-2'
    : 'hover:ring-2 hover:ring-blue-300/70';

  function openPicker(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setSelected(path);
    setOpen(true);
  }

  if (!src) {
    return (
      <Fragment>
        <button
          type="button"
          onClick={openPicker}
          className={`${className ?? ''} flex min-h-[8rem] items-center justify-center rounded-md border-2 border-dashed border-slate-400 bg-slate-50/80 text-sm font-medium text-slate-500 hover:bg-slate-100`}
          style={style}
        >
          {emptyLabel}
        </button>
        {open &&
          renderVideoPicker &&
          renderVideoPicker({
            path,
            currentValue: effective,
            onClose: () => setOpen(false),
          })}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <span className={`relative inline-block ${ringClass}`} style={{ lineHeight: 0 }}>
        <video
          ref={ref}
          src={src}
          className={className}
          style={style}
          {...videoProps}
        />
        {/* Edit overlay button -keeps native video controls usable while in edit mode */}
        <button
          type="button"
          onClick={openPicker}
          className="absolute right-2 top-2 z-10 rounded-md bg-black/70 px-2 py-1 text-xs font-medium text-white shadow hover:bg-black/85"
        >
          Editar vídeo
        </button>
      </span>
      {open &&
        renderVideoPicker &&
        renderVideoPicker({
          path,
          currentValue: effective,
          onClose: () => setOpen(false),
        })}
    </Fragment>
  );
});
